import React, { useEffect, useState } from 'react';
import { Menu, MenuItem, Grid, ListItemText, Button } from '@mui/material';
import { IconPin } from '@tabler/icons';
import { IRecordType } from 'types/recordType';
import { openInANewTabDFVAction, openInThisTabDFVAction } from '../utils';
import { useUserPreferences } from 'hooks';

export type DataFocusViewButtonMenuProps = {
    open: boolean;
    anchorEl: null | HTMLElement;
    onClose: () => void;
    recordId: string;
    recordType: IRecordType;
    onOpenInAPopUp?: () => void;
    transformOrigin?: { vertical: 'top' | 'bottom' | 'center'; horizontal: 'left' | 'center' | 'right' };
    anchorOrigin?: { vertical: 'top' | 'bottom' | 'center'; horizontal: 'left' | 'center' | 'right' };
    onDefaultPrefChange?: (pref: DataFocusViewPref) => void;
};

export const DATA_FOCUS_VIEW_PREF = {
    OPEN_IN_THIS_TAB: 'OPEN_IN_THIS_TAB',
    OPEN_IN_A_NEW_TAB: 'OPEN_IN_A_NEW_TAB',
    OPEN_IN_A_POP_UP_WINDOW: 'OPEN_IN_A_POP_UP_WINDOW'
};
export type DataFocusViewPref = keyof typeof DATA_FOCUS_VIEW_PREF;

export const DFV_MENU_PREF_KEY = 'dataFocusViewMenuPref';

export const DataFocusViewButtonMenu = ({
    open,
    anchorEl,
    onClose,
    recordId,
    recordType,
    onOpenInAPopUp,
    anchorOrigin,
    transformOrigin,
    onDefaultPrefChange
}: DataFocusViewButtonMenuProps) => {
    const [selectedPref, setSelectedPref] = useState<DataFocusViewPref>('OPEN_IN_THIS_TAB');
    const { savePreferences } = useUserPreferences(recordType.name, +recordType.id);

    // Handlers for each option
    const handleOpenInThisTab = () => {
        onClose();
        openInThisTabDFVAction(recordId, recordType);
    };
    const handleOpenInNewTab = () => {
        onClose();
        openInANewTabDFVAction(recordId, recordType);
    };

    const handleOpenInPopup = () => {
        onOpenInAPopUp?.();
    };

    // When pin is selected, save preference but DO NOT execute the handler
    const handlePinClick = (option: (typeof menuOptions)[number], e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedPref(option.value as DataFocusViewPref);
        onDefaultPrefChange?.(option.value as DataFocusViewPref);
        localStorage.setItem(DFV_MENU_PREF_KEY, option.value);

        // Dispatch custom event to notify the change in the same tab
        window.dispatchEvent(
            new CustomEvent('localStorageChange', {
                detail: { key: DFV_MENU_PREF_KEY, value: option.value }
            })
        );
        savePreferences();
    };

    useEffect(() => {
        const pref = localStorage.getItem(DFV_MENU_PREF_KEY) as DataFocusViewPref | null;
        if (pref && Object.values(DATA_FOCUS_VIEW_PREF).includes(pref)) {
            setSelectedPref(pref as DataFocusViewPref);
        }
    }, []);

    // Menu options (inside component, not global const)
    const menuOptions = [
        { label: 'Open in this tab', value: DATA_FOCUS_VIEW_PREF.OPEN_IN_THIS_TAB, handler: handleOpenInThisTab },
        { label: 'Open in a new tab', value: DATA_FOCUS_VIEW_PREF.OPEN_IN_A_NEW_TAB, handler: handleOpenInNewTab },
        { label: 'Open in a pop-up window', value: DATA_FOCUS_VIEW_PREF.OPEN_IN_A_POP_UP_WINDOW, handler: handleOpenInPopup }
    ];

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            MenuListProps={{ sx: { p: 0 } }}
            PaperProps={{ sx: { borderRadius: '10px', minWidth: 280, width: 'auto' } }}
            sx={{ '& .MuiMenu-paper': { width: 'auto !important' } }}
            transformOrigin={transformOrigin}
            anchorOrigin={anchorOrigin}
        >
            {menuOptions.map((option, idx) => (
                <MenuItem
                    key={option.value}
                    sx={(theme) => ({
                        bgcolor: '#F5F6F7',
                        p: 1,
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: theme.palette.secondary.light },
                        borderBottom: idx !== menuOptions.length - 1 ? '1px solid #D9D9D9' : 'none'
                    })}
                    onClick={option.handler}
                >
                    <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
                        <Grid item style={{ flex: 1, minWidth: 0 }}>
                            <ListItemText primary={option.label} sx={{ '& span': { color: '#54595E', textOverflow: 'ellipsis' } }} />
                        </Grid>
                        <Grid item>
                            <Button
                                sx={(theme) => ({
                                    borderRadius: '8px',
                                    minWidth: '30px',
                                    height: '30px',
                                    bgcolor: selectedPref === option.value ? theme.palette.secondary.main : '#FFFFFF',
                                    boxShadow: 'none',
                                    ml: 1,
                                    transition: 'background 0.2s',
                                    '&:hover': {
                                        bgcolor: theme.palette.secondary.main,
                                        '& svg': {
                                            color: '#FFFFFF !important',
                                            stroke: '#FFFFFF !important'
                                        }
                                    }
                                })}
                                variant="contained"
                                size="small"
                                onClick={(e) => handlePinClick(option, e)}
                            >
                                <IconPin size={16} color={selectedPref === option.value ? '#FFFFFF' : '#37326E'} />
                            </Button>
                        </Grid>
                    </Grid>
                </MenuItem>
            ))}
        </Menu>
    );
};
